// Firebase configuration and authentication setup
import { initializeApp, FirebaseApp } from "firebase/app";
import {
  getAuth,
  Auth,
  signInWithRedirect,
  signInWithPopup,
  getRedirectResult,
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import { getStorage, FirebaseStorage } from "firebase/storage";
import { getFirestore, Firestore } from "firebase/firestore";

interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  appId: string;
}

// Default config as fallback - these are placeholder values
// Real config should come from the API settings
const defaultConfig: FirebaseConfig = {
  apiKey: "your-api-key",
  authDomain: "your-app.firebaseapp.com",
  projectId: "your-project-id",
  storageBucket: "your-app.appspot.com",
  appId: "your-app-id",
};

// Firebase instances (will be initialized dynamically)
let app: FirebaseApp;
let _auth: Auth;
let _storage: FirebaseStorage;
let _db: Firestore;
let isInitialized = false;

// Initialize Firebase with dynamic config
export function initializeFirebase(config?: FirebaseConfig) {
  // Don't re-initialize with placeholder config
  if (!config || config.projectId === 'your-project-id') {
    console.warn('⚠️ Refusing to initialize auth with placeholder/missing config');
    return;
  }

  // Allow re-initialization if config has changed (for development hot-reload)
  if (isInitialized) {
    console.log('🔥 Auth already initialized, skipping re-initialization');
    return;
  }

  console.log('🔥 Initializing auth with config:', { projectId: config.projectId });

  app = initializeApp(config);
  _auth = getAuth(app);
  _storage = getStorage(app);
  _db = getFirestore(app);
  isInitialized = true;

  console.log('✅ Auth initialized successfully');
}

// Getters that ensure Firebase is initialized
export const getFirebaseAuth = (): Auth => {
  if (!isInitialized) {
    throw new Error('Auth service not initialized. Make sure SettingsProvider loads settings first.');
  }
  return _auth;
};

export const getFirebaseStorage = (): FirebaseStorage => {
  if (!isInitialized) {
    throw new Error('Auth service not initialized. Make sure SettingsProvider loads settings first.');
  }
  return _storage;
};

export const getFirebaseDb = (): Firestore => {
  if (!isInitialized) {
    throw new Error('Auth service not initialized. Make sure SettingsProvider loads settings first.');
  }
  return _db;
};

export const getFirebaseApp = (): FirebaseApp => {
  if (!isInitialized) {
    throw new Error('Auth service not initialized. Make sure SettingsProvider loads settings first.');
  }
  return app;
};

// Export for backward compatibility
export const auth = new Proxy({} as Auth, {
  get: (target, prop) => {
    return (getFirebaseAuth() as any)[prop];
  }
});

export const storage = new Proxy({} as FirebaseStorage, {
  get: (target, prop) => {
    return (getFirebaseStorage() as any)[prop];
  }
});

export const db = new Proxy({} as Firestore, {
  get: (target, prop) => {
    return (getFirebaseDb() as any)[prop];
  }
});

export const googleProvider = new GoogleAuthProvider();
export const appleProvider = new OAuthProvider("apple.com");

// Configure Google provider
googleProvider.addScope("email");
googleProvider.addScope("profile");

// Configure Apple provider
appleProvider.addScope("email");
appleProvider.addScope("name");

// Google Sign In with popup (better UX than redirect)
export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(getFirebaseAuth(), googleProvider);
    
    // Extract Google OAuth credential to get access token
    const credential = GoogleAuthProvider.credentialFromResult(result);
    const providerToken = credential?.accessToken || null;
    
    // Return result with provider token attached
    return {
      ...result,
      providerToken,
      providerId: 'google.com'
    };
  } catch (error: any) {
    // If popup is blocked, fall back to redirect
    if (error.code === "auth/popup-blocked") {
      console.log("Popup blocked, falling back to redirect");
      return await signInWithGoogleRedirect();
    }
    throw error;
  }
};

// Google Sign In with redirect (fallback if popup is blocked)
export const signInWithGoogleRedirect = () => {
  return signInWithRedirect(getFirebaseAuth(), googleProvider);
};

// Handle redirect result (call this on app initialization)
export const handleAuthRedirect = () => {
  return getRedirectResult(getFirebaseAuth());
};

// Extract the OAuth provider access token from a UserCredential (popup or redirect)
export const extractProviderToken = (result: any): string | null => {
  try {
    const credential = GoogleAuthProvider.credentialFromResult(result);
    return credential?.accessToken || null;
  } catch {
    return null;
  }
};

// Sign out
export const firebaseSignOut = () => {
  return signOut(getFirebaseAuth());
};

// Apple Sign In with popup (better UX than redirect)
export const signInWithApple = async () => {
  try {
    const result = await signInWithPopup(getFirebaseAuth(), appleProvider);
    
    // Extract Apple OAuth credential to get identity token
    const credential = OAuthProvider.credentialFromResult(result);
    const providerToken = credential?.idToken || credential?.accessToken || null;
    
    // Return result with provider token attached
    return {
      ...result,
      providerToken,
      providerId: 'apple.com'
    };
  } catch (error: any) {
    // If popup is blocked, fall back to redirect
    if (error.code === "auth/popup-blocked") {
      console.log("Popup blocked, falling back to redirect");
      return await signInWithAppleRedirect();
    }
    throw error;
  }
};

// Apple Sign In with redirect (fallback if popup is blocked)
export const signInWithAppleRedirect = () => {
  return signInWithRedirect(getFirebaseAuth(), appleProvider);
};

// Translate a Firebase Auth error into a user-facing message. Firebase's own
// `error.message` (e.g. "Firebase: Error (auth/error-code:-39).") leaks SDK
// internals that mean nothing to end users, so map known codes to plain
// language and fall back to a generic, non-technical message for the rest.
export function getFirebaseAuthErrorMessage(error: any, fallback = "Something went wrong. Please try again."): string {
  const code = error?.code as string | undefined;
  if (!code || !code.startsWith("auth/")) {
    return error?.message || fallback;
  }
  switch (code) {
    case "auth/invalid-phone-number":
      return "That phone number doesn't look valid. Include the country code, e.g. +15551234567.";
    case "auth/missing-phone-number":
      return "Enter a phone number.";
    case "auth/invalid-verification-code":
      return "That code is incorrect. Please check and try again.";
    case "auth/code-expired":
      return "That code has expired. Request a new one.";
    case "auth/too-many-requests":
    case "auth/quota-exceeded":
      return "SMS verification is temporarily unavailable due to high demand. Please try again later, or use an authenticator app instead.";
    case "auth/captcha-check-failed":
      return "Verification check failed. Please refresh the page and try again.";
    case "auth/network-request-failed":
      return "Network error. Check your connection and try again.";
    default:
      console.error("Unhandled Firebase auth error:", code, error?.message);
      return "We couldn't send the verification code right now. Please try again in a moment, or use an authenticator app instead.";
  }
}

// Auth state observer
export const onAuthStateChange = (callback: (user: any) => void) => {
  if (!isInitialized) {
    console.warn('⚠️ Auth not initialized, cannot set up auth listener');
    // Return a no-op unsubscribe function
    return () => {};
  }
  return onAuthStateChanged(getFirebaseAuth(), callback);
};
